import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";

// Actions
import { getBasicCampaignDetails } from "../../../actions/campaign/CampaignBasicInfoActions";
import {
  creativeUploadFileItems,
  getCampaignMedia,
  openCampaignMediaCreativesForm,
} from "../../../actions/campaign-media/CampaignMediaActions";

// Constants and Utils
import { defaultPagination } from "../../../constants/UrlConstants";
import {
  FormDataTargets,
  ImageFileTypes,
  VideoFileTypes,
} from "../../../constants/GeneralConstants";
import { selectFile } from "../../../common-utils/file-utils/FileUtils";
import { getCitySpecificMediaList } from "../media-selection/MediaContainerUtil";
import { getSelectedCityName } from "../../utils/campaignPlanningUtil";

// Components
import Spinner from "../../../components/spinner/Spinner";
import PageHeader from "../../../mavin/components/page-header/PageHeader";
import TableHeaders from "../../../components/table/TableHeaders";
import CampaignSidebar from "../../../components/campaign-sidebar/CampaignSidebar";
import CheckBox from "../../../components/check-box/CheckBox";
import { CampaignDetails } from "../components/HeaderNav";
import { MediaInfo } from "../components/MediaRow";
import CampaignMediasCreativesForm from "./CampaignMediasCreativesForm";

// Page Components
function SelectUnSelectAllMedia({
  mediaIds = [],
  selectedMediaIds = [],
  setSelectedMediaIds = () => {},
}) {
  // check any media checked
  const isAnyMediaChecked = selectedMediaIds.length > 0;

  // checking all checked
  const isAllChecked =
    selectedMediaIds.length > 0 &&
    selectedMediaIds.length === mediaIds.length &&
    selectedMediaIds.every((id) => mediaIds.includes(id));

  // function
  function handleAllChange() {
    // uncheck all
    if (isAnyMediaChecked) {
      setSelectedMediaIds([]);
    } else {
      // check all
      setSelectedMediaIds(mediaIds);
    }
  }

  return (
    <th className="col-1">
      <CheckBox
        boxStyle="pl-0"
        checked={isAllChecked}
        onChange={handleAllChange}
      />
    </th>
  );
}

// Table Headers
function getTableHeaders(mediaIds, selectedMediaIds, setSelectedMediaIds) {
  return [
    {
      element: (
        <SelectUnSelectAllMedia
          mediaIds={mediaIds}
          selectedMediaIds={selectedMediaIds}
          setSelectedMediaIds={setSelectedMediaIds}
        />
      ),
    },
    {
      title: {
        displayName: "Site Name",
        className: "col-3",
      },
    },
    {
      title: {
        displayName: "Road Stretch",
        className: "col-3",
      },
    },
    {
      title: {
        displayName: "Device ID",
        className: "col-2 text-center",
      },
    },
    {
      title: {
        displayName: "Static/Digital",
        className: "col-1 text-center",
      },
    },
    {
      title: {
        displayName: "Actions",
        className: "col-2 text-center",
      },
    },
  ];
}

function CreativeMediaDetailsRow({
  media = {},
  selectedMediaIds = [],
  setSelectedMediaIds = () => {},
}) {
  const dispatch = useDispatch();

  // data
  const {
    campaignId,
    id,
    sourceId,
    roadStretchName,
    isDigital = false,
  } = media;

  // checked
  const isMediaChecked = selectedMediaIds.includes(id);

  // function
  function handleCheck(selectedId) {
    // add selected
    if (!isMediaChecked) {
      setSelectedMediaIds((prev) => [...prev, selectedId]);
      return;
    }

    // remove selected
    const updatedIds = selectedMediaIds.filter((id) => id !== selectedId);
    setSelectedMediaIds(updatedIds);
  }

  function handleViewCreativesFrom() {
    dispatch(openCampaignMediaCreativesForm({ campaignId, id }));
  }

  return (
    <tr>
      <td>
        <CheckBox
          boxStyle="pl-0"
          checkBoxKey={id}
          showLabel={false}
          checked={isMediaChecked}
          onChange={(_e, id) => handleCheck(id)}
        />
      </td>
      <MediaInfo media={media} />
      <td>{roadStretchName}</td>
      <td className="text-center">{sourceId || "--"}</td>
      <td className="text-center">{isDigital ? "Digital" : "Static"}</td>
      <td className="text-center">
        <button
          type="button"
          className="btn btn-link shadow-none"
          data-toggle="modal"
          data-target={`#${FormDataTargets.campaignMediaCreativesForm}`}
          onClick={handleViewCreativesFrom}
        >
          View Creatives
        </button>
      </td>
    </tr>
  );
}

// get Are All Selected Digital
function getAreAllSelectedDigital(selectedMediaIds = [], mediaList = []) {
  // filter selected medias
  const selectedMediaList = mediaList.filter((media) =>
    selectedMediaIds.includes(media.id)
  );

  // check all selected are digital
  const areAllSelectedDigital = selectedMediaList.every(
    (media) => media.isDigital
  );

  return areAllSelectedDigital;
}

function TableHeaderSection({
  cityName,
  selectedMediaIds = [],
  setSelectedMediaIds,
  citySpecificMediaList = [],
}) {
  const dispatch = useDispatch();
  const { campaignId } = useParams();

  // upload loading
  const creativeUploadLoading = useSelector(
    (state) => state.campaignMedia.creativeUploadLoading
  );

  // checking any media selected or not
  const isAnyMediaChecked = selectedMediaIds.length > 0;

  // function
  async function handleCreativeUpload() {
    // checking all digital selected or not
    const areAllSelectedDigital = getAreAllSelectedDigital(
      selectedMediaIds,
      citySpecificMediaList
    );

    // final types
    let finalFileTypes = [];

    // static media (image)
    if (!areAllSelectedDigital) {
      finalFileTypes = ImageFileTypes;
    } else {
      // digital media (image + video)
      finalFileTypes = [...ImageFileTypes, ...VideoFileTypes];
    }

    // upload dialog
    const files = await selectFile(true, finalFileTypes);

    // dispatch
    dispatch(
      creativeUploadFileItems(
        campaignId,
        selectedMediaIds,
        files,
        setSelectedMediaIds
      )
    );
  }

  return (
    <div className="d-flex align-items-center justify-content-between">
      <h4>Media Sites : {cityName || ""}</h4>
      <button
        type="button"
        className="btn btn-link shadow-none p-0 pr-1"
        onClick={handleCreativeUpload}
        disabled={!isAnyMediaChecked || creativeUploadLoading}
      >
        + Creative Upload
        {creativeUploadLoading && (
          <Spinner className="spinner-border-sm ml-1" />
        )}
      </button>
    </div>
  );
}

/**
 * Campaign Medias Creatives Page
 */
export default function CampaignMediasCreativesPage() {
  const dispatch = useDispatch();
  const { campaignId, cityId } = useParams();

  // State
  const [selectedMediaIds, setSelectedMediaIds] = useState([]);

  // Selector
  const campaign = useSelector((state) => state.c_campaignBasicInfo.campaign);
  const basicCampaignDetailsLoading = useSelector(
    (state) => state.c_campaignBasicInfo.campaignLoading
  );

  const campaignMedias = useSelector(
    (state) => state.campaignMedia.campaignMedia
  );
  const campaignMediasLoading = useSelector(
    (state) => state.campaignMedia.campaignMediaLoading
  );

  // creatives modal
  const openCreativesModal = useSelector(
    (state) => state.campaignMedia.openCreativesModal
  );

  // city specific medias
  const citySpecificMediaList = getCitySpecificMediaList(
    campaignMedias,
    cityId
  );
  const mediaIds = citySpecificMediaList.map((media) => media.id);

  // selected city name
  const cityName = getSelectedCityName(campaign, cityId);

  const { pageNo, pageSize } = defaultPagination;
  useEffect(() => {
    dispatch(getBasicCampaignDetails(campaignId));
    dispatch(getCampaignMedia(campaignId, pageNo, pageSize));
  }, [dispatch, campaignId, cityId]);

  // page loading
  const pageLoading = basicCampaignDetailsLoading || campaignMediasLoading;
  if (pageLoading) {
    return <Spinner className="spinner-center mt-2" />;
  }

  // Sidebar Settings
  const citySelected = { cityId };
  const redirectLinkObj = {
    campaignOverviewLink: ``,
    citySummaryLink: `/campaign/${campaignId}/city/${cityId}/creative-upload`,
  };

  // Page Title
  const pageTitle = <CampaignDetails campaign={campaign} />;

  // Table Headers
  const tableHeaders = getTableHeaders(
    mediaIds,
    selectedMediaIds,
    setSelectedMediaIds
  );

  return (
    <>
      {/* SideBar */}
      <CampaignSidebar
        campaign={campaign}
        redirectLinkObj={redirectLinkObj}
        citySelected={citySelected}
        isHideOverview={true}
      />

      <div className="content-wrapper">
        <PageHeader title={pageTitle} />

        {/* Page-Content */}
        <div className="page-content">
          <div className="table-responsive">
            <TableHeaderSection
              cityName={cityName}
              selectedMediaIds={selectedMediaIds}
              setSelectedMediaIds={setSelectedMediaIds}
              citySpecificMediaList={citySpecificMediaList}
            />

            <table className="table table-media">
              <TableHeaders tableHeaders={tableHeaders} headerClass={"thead"} />
              <tbody>
                {citySpecificMediaList.map((eachMedia) => (
                  <CreativeMediaDetailsRow
                    key={eachMedia.id}
                    media={eachMedia}
                    selectedMediaIds={selectedMediaIds}
                    setSelectedMediaIds={setSelectedMediaIds}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* MODALS */}
        {openCreativesModal && <CampaignMediasCreativesForm />}
      </div>
    </>
  );
}
